import { InfosecLevel } from "@istari/istari-client";
import { useMemo } from "react";
import { useAuth } from "react-oidc-context";

import { useGetPlatformInfosecSettings, useListInfosecLevels } from "~/api/useInfosecLevels";
import { useUsers } from "~/user/data-source/use-users";

interface UseAllowedInfosecLevelsOptions {
  ignoreUserLevel?: boolean;
  enabled?: boolean;
}

/** Returns infosec levels the current user is allowed to assign, filtered by platform and optional constraints. */
export function useAllowedInfosecLevels(
  systemInfosecLevel?: InfosecLevel | null,
  options?: UseAllowedInfosecLevelsOptions,
) {
  const auth = useAuth();
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  const infosecLevelsQuery = useListInfosecLevels({ enabled: options?.enabled });
  const orgUsersQuery = useUsers();

  return useMemo(() => {
    const levels = infosecLevelsQuery.data;
    if (!levels) return [];
    const sorted = [...levels].sort((a, b) => a.order - b.order);

    const platformMaxLevelOrder = platformSettingsQuery.data?.maxLevel?.order;
    const systemMaxLevelOrder = systemInfosecLevel?.order;

    const currentUserSubId = auth?.user?.profile.sub;
    const currentUser = orgUsersQuery.data?.find((orgUser) => orgUser.providerUserId === currentUserSubId);
    const userAssignedLevelOrder = options?.ignoreUserLevel ? undefined : currentUser?.infosecLevel?.order;

    const constraints = [platformMaxLevelOrder, systemMaxLevelOrder, userAssignedLevelOrder].filter(
      (order): order is number => order !== undefined && order !== null,
    );

    if (constraints.length === 0) return sorted;

    const effectiveMaxOrder = Math.min(...constraints);

    return sorted.filter((level) => level.order <= effectiveMaxOrder);
  }, [
    infosecLevelsQuery.data,
    platformSettingsQuery.data?.maxLevel?.order,
    systemInfosecLevel,
    auth?.user?.profile.sub,
    orgUsersQuery.data,
    options?.ignoreUserLevel,
  ]);
}
